























































































































































import { PropType } from 'vue';
import { defineComponent, computed, toRefs, ref, onMounted, Ref, watch, nextTick } from '@vue/composition-api';
import { AdgroupFormData, CampaignFormData, AdgroupConfig, AdgroupListItem, CreateMode } from '@tt4b/ttam-enums';
import {
  StateEnum,
  useAdgroupBudgetValidate,
  useStdAccountStore,
  useStdAdgroupIs,
  useStore,
  useCreativeActionTypeIs,
} from '@tt4b/ttam-hooks';
import { useStdLocation } from './../location/hooks/index';
import { trackEvent } from './../../../utils/index';
import { convert2UTC } from '@tt4b/ttam-utils';
import Schedule from './index';
import durationScheduleImage from '@tt4b/ttam-assets/src/svgs/duration-schedule.svg';
import { i18n } from '@tt4b/ttam-i18n';

const name = 'ttam-schedule-compose';

export default defineComponent({
  name,
  components: {
    TtamScheduleSplittestLabelHint: Schedule.TtamScheduleSplittestLabelHint,
    TtamScheduleSplittestLabel: Schedule.TtamScheduleSplittestLabel,
    TtamScheduleLabel: Schedule.TtamScheduleLabel,
    TtamScheduleContinuousRadio: Schedule.TtamScheduleContinuousRadio,
    TtamScheduleDaterangeRadio: Schedule.TtamScheduleDaterangeRadio,
    TtamScheduleContinuousTimepicker: Schedule.TtamScheduleContinuousTimepicker,
    TtamScheduleDaterangeTimepicker: Schedule.TtamScheduleDaterangeTimepicker,
    TtamScheduleTimezoneDesc: Schedule.TtamScheduleTimezoneDesc,
    TtamScheduleTimeConverterDialog: Schedule.TtamScheduleTimeConverterDialog,
    TtamScheduleDateDurationTimepicker: Schedule.TtamScheduleDateDurationTimepicker,
    TtamScheduleDateDurationRadio: Schedule.TtamScheduleDateDurationRadio,
    durationScheduleImage,
  },
  props: {
    campaignData: {
      type: Object as PropType<CampaignFormData>,
      default: () => ({}),
      required: true,
    },
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
      required: true,
    },
    adgroupConfig: {
      type: Object as PropType<AdgroupConfig>,
      default: () => ({}),
      required: true,
    },
    cboAdgroupList: {
      type: Array as PropType<AdgroupListItem[]>,
      default: () => [],
    },
    createMode: {
      type: String as PropType<CreateMode>,
      default: CreateMode.CREATE,
    },
    spcCreateMode: {
      type: String as PropType<CreateMode>,
      default: CreateMode.NONE,
    },
    isSplitTestEnabled: {
      type: Boolean,
      default: false,
    },
    getAccountNowTimestamp: {
      type: Function,
      default: () => () => 0,
    },
    timezoneOffset: {
      type: Number,
      default: 0,
    },
    disableScrollControl: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const durationTooltipHoverStartTime = ref(0);
    const {
      adgroupData,
      campaignData,
      adgroupConfig,
      createMode,
      spcCreateMode,
      isSplitTestEnabled,
      getAccountNowTimestamp,
    } = toRefs(props);
    const { timezone } = useStdAccountStore();
    const { countryIds, provinceIds, cityIds } = useStdLocation({
      adgroupData,
    });
    const { isEdit } = useCreativeActionTypeIs({
      createMode,
      adgroupFormData: adgroupData,
    });
    const locationData = computed(() => ({
      countryIds: countryIds.value,
      provinceIds: provinceIds.value,
      cityIds: cityIds.value,
      particleLocations: adgroupData.value.particleLocations,
    }));

    const { getStore, state } = useStore();
    const { run: getTimezoneList } = getStore(StateEnum.timezoneList);
    const timezoneList = computed(() => state[StateEnum.timezoneList].data?.value?.timezone_list ?? []);
    const scheduleRef = ref();
    let openTimeConverterTime = 0;
    const timeConverterVisible = ref(false);
    const { isLiveAds } = useStdAdgroupIs({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
    });

    const bottomShortcutsOptions = computed(() => {
      const bottomShortcuts = [
        {
          text: i18n.t('module_adgroup_schedule_set_ten_minute'),
          onClick(picker) {
            // 获取账号时区时间戳
            // eslint-disable-next-line @typescript-eslint/ban-types
            picker.$emit('pick', new Date((getAccountNowTimestamp as Ref<Function>).value() + 10 * 60 * 1000));
          },
        },
      ];
      return isEdit.value
        ? {
            bottomShortcuts,
          }
        : {};
    });

    // 用于记录正在打开的timezone converter弹窗的场景scenario
    const dialogScenario = ref('');

    const aadvid = computed(() => state[StateEnum.baseInfo].value.aadvid);

    const timezoneConverterExposeTea = 'timezone_converter_popup_entrance_impression';
    const timezoneConverterExposeTeaParam = {
      'data-tea-adv_id': aadvid.value,
      'data-tea-objective_type': adgroupData.value.objectiveType,
      'data-tea-std_event_type': 'click',
      'data-tea-std_site_name': 'ttam',
      'data-tea-std_page_name': '/i18n/creation/adgroup',
      'data-tea-std_module_name': 'Budget & Schedule',
    };

    const locationCount = computed(() => adgroupData.value.particleLocations.length ?? 0);
    const hasLocation = computed(() => Boolean(adgroupData.value.particleLocations.length));
    const timezoneStr = computed(() => convert2UTC(timezone.value, timezoneList.value));

    const _scheduleType = computed({
      get: () => props?.adgroupData?.scheduleType,
      set: v => {
        emit('type-change', v);
        nextTick(() => {
          scheduleRef.value?.validate && scheduleRef.value.validate('change');
        });
      },
    });

    const _scheduleStartTime = computed({
      get: () => props?.adgroupData?.startTime,
      set: v => {
        emit('starttime-change', v);
      },
    });
    const _scheduleEndTime = computed({
      get: () => props?.adgroupData?.endTime,
      set: v => {
        emit('endtime-change', v);
        nextTick(() => {
          scheduleRef.value.validate && scheduleRef.value.validate('change');
        });
      },
    });
    const _scheduleDuration = computed({
      get: () => props?.adgroupData?.durationTimeRange,
      set: v => {
        trackEvent('live_ads_delivery_duration_duration', {
          ...timezoneConverterExposeTeaParam,
          scenario: 'duration',
          std_event_type: 'impression',
        });
        emit('durationtime-change', Number(v));
      },
    });

    watch(
      () => _scheduleType.value,
      () => {
        scheduleRef.value?.clearValidate && scheduleRef.value?.clearValidate();
      },
    );

    const { endTimeDisabled } = Schedule.useDaterangeTimepickerPropsEndTimeDisabled({
      adgroupConfig,
    });

    const { startTimeDisabled } = Schedule.useDaterangeTimepickerPropsStartTimeDisabled({
      adgroupConfig,
    });

    const { durationTimeDisabled } = Schedule.useDatedurationTimepickerPropsDurationDisabled({
      adgroupConfig,
    });
    const { disabled: continuousRadioDisabled } = Schedule.useContinuousRadioPropsDisabled({
      adgroupData,
      campaignData,
      isSplitTestEnabled,
    });

    const { disabled: daterangeDisabled } = Schedule.useDaterangeRadioPropsDisabled({
      adgroupData,
      campaignData,
      isSplitTestEnabled,
    });

    const { visible: dateDurationRadioVisible } = Schedule.useDateDurationRadioPropsVisible({
      adgroupData,
      campaignData,
    });
    const { visible: dateRangeTimePickerVisible } = Schedule.useDaterangeTimepickerPropsVisible({
      adgroupData,
      campaignData,
    });

    const { label: dateRangeRadioLabel } = Schedule.useDaterangeRadioPropsLabel();

    const { hint: dateRangeRadioHint } = Schedule.useDaterangeRadioPropsHint({
      adgroupData,
      campaignData,
    });

    const { visible: dateDurationTimePickerVisible } = Schedule.useDateDurationTimepickerPropsVisible({
      adgroupData,
    });

    const { visible: continuousTimePickerVisible } = Schedule.useContinuousTimepickerPropsVisible({
      adgroupData,
      campaignData,
    });

    const { timeRangeValidator } = useAdgroupBudgetValidate({
      campaignFormData: campaignData,
      adgroupEditConfig: adgroupConfig,
      getAccountNowTimestamp,
      createMode,
      spcCreateMode,
    });

    // TODO 依赖splittest
    // const isSplitTestEnabled = ref(false);

    const onOpenDialog = scenario => {
      timeConverterVisible.value = true;
      dialogScenario.value = scenario;
      openTimeConverterTime = new Date().getTime();
      trackEvent('timezone_converter_tip_click', {
        scenario,
        adv_id: aadvid.value,
        objective_type: adgroupData.value.objectiveType,
        std_event_type: 'click',
        std_site_name: 'ttam',
        std_page_name: '/i18n/creation/adgroup',
        std_module_name: 'Budget & Schedule',
      });
    };

    const onCloseDialog = () => {
      timeConverterVisible.value = false;
      trackEvent('timezone_converter_popup_stay_page', {
        scenario: dialogScenario.value,
        objective_type: adgroupData.value.objectiveType,
        duration: new Date().getTime() - openTimeConverterTime,
        std_event_type: 'duration',
        std_site_name: 'ttam',
        std_page_name: 'timezone_converter_popup',
        std_module_name: 'Budget & Schedule',
      });
      // track后要清空上一次的scenario记录
      dialogScenario.value = '';
    };
    onMounted(() => {
      // 其实感觉这行代码比较冗余
      getTimezoneList();
    });

    const onDurationTooltipMouseEnter = () => {
      durationTooltipHoverStartTime.value = new Date().getTime();
    };

    const onDurationTooltipMouseLeave = () => {
      const duration = new Date().getTime() - durationTooltipHoverStartTime.value;
      trackEvent('live_ads_delivery_duration_popup_view_rention', {
        std_event_type: 'hover',
        std_site_name: 'TTAM',
        std_page_name: '/i18n/creation/adgroup',
        std_module_name: 'Budget & Schedule',
        duration,
        scenario: 'set_duration',
        objective_type: adgroupData.value.objectiveType,
      });
    };

    return {
      name,
      isLiveAds,
      timezone,
      timezoneList,
      _scheduleType,
      scheduleRef,
      _scheduleStartTime,
      _scheduleEndTime,
      _scheduleDuration,
      endTimeDisabled,
      startTimeDisabled,
      daterangeDisabled,
      continuousRadioDisabled,
      continuousTimePickerVisible,
      dateRangeTimePickerVisible,
      dateDurationTimePickerVisible,
      dateDurationRadioVisible,
      durationTimeDisabled,
      dateRangeRadioLabel,
      dateRangeRadioHint,
      bottomShortcutsOptions,

      timeRangeValidator,
      hasLocation,
      locationCount,
      onOpenDialog,
      onCloseDialog,
      timeConverterVisible,
      timezoneStr,
      locationData,
      dialogScenario,
      timezoneConverterExposeTea,
      timezoneConverterExposeTeaParam,
      durationScheduleImage,
      onDurationTooltipMouseEnter,
      onDurationTooltipMouseLeave,
    };
  },
});
